import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { getSpotConfig, getV1Config, setSpotConfig, setV1Config } from '@host-panel/request/config';
import { INIT_CONFIG_FIELDS, MODERATION_SETTINGS_CONFIG_FIELDS } from '@host-panel/request/config/constants';

import ErrorNotifier from '../scripts/services/error-notifier';
import { updateInitConfig } from '../scripts/init-config/initConfigSlice';
import fetchHighlightTags from '../scripts/components/moderation-v2/comments-and-posts/state/actions/fetch-highligh-tags-config';
import {
  addAuthorizedDomainRequest,
  fetchAuthorizedDomainsRequest,
  removeAuthorizedDomainRequest
} from '../scripts/components/settings/api/authorized-domains';
import { showToast } from '../components/toast/toastSlice';
import { ShowToastPayload } from '../components/toast/toastTypes';
import { getModerationPreferences } from '../scripts/actions/spot/api/get-moderation-preferences';

import {
  addAuthorizedDomain,
  editSpot,
  fetchAuthorizedDomains,
  fetchInitSpotData,
  fetchSpotModerationData,
  fetchSpotPolicyData,
  removeAuthorizedDomain,
  setStrictExternalUrlsFlag
} from './spotsActions';
import {
  EditSpotPayload,
  FetchAuthorizedDomainsResponse,
  FetchInitSpotDataResponse,
  FetchModerationPreferenceResponse,
  SetStrictExternalUrlsFlagPayload,
  Spot
} from './spotsTypes';
import {
  getUpdatedConnectTypes,
  hasConnectTypes,
  mapToSpotProps,
  parseInitSpotData,
  toKeyValueArray
} from './spotsUtils';
import { updateSpot } from './spotsSlice';
import { State } from 'state';
import { resetVerification } from '../auth/authSlice';

function* fetchInitSpotDataSaga(action: PayloadAction<string>) {
  const spotId = action.payload;

  try {
    const response: FetchInitSpotDataResponse = yield call(getV1Config, spotId, INIT_CONFIG_FIELDS);
    const parsed = parseInitSpotData(response);

    yield put(updateSpot(parsed));
    yield put(updateInitConfig({ ltr: parsed.ltr }));
    yield put(resetVerification());
  } catch (reason) {
    ErrorNotifier.notifyError(`Failed to fetch spot`);
  }
}

function* editSpotSaga(action: PayloadAction<EditSpotPayload>) {
  const { spotId, data, sendEvent } = action.payload;

  const configuration = toKeyValueArray(data);
  let success = true;

  try {
    const response = yield call(setV1Config, spotId, configuration);

    let update: Partial<Spot>;

    if (hasConnectTypes(response)) {
      const connectTypes = yield select((state: State) => state.spots.map[spotId]?.connectTypes) || [];
      update = getUpdatedConnectTypes(response, connectTypes);
    } else {
      update = mapToSpotProps(response);
    }

    yield put(updateSpot(update));
  } catch (reason) {
    success = false;
    ErrorNotifier.notifyError(`Failed to edit spot`);
  }

  sendEvent?.(success);
}

function* fetchAuthorizedDomainsSaga() {
  try {
    const response: FetchAuthorizedDomainsResponse = yield call(fetchAuthorizedDomainsRequest);
    yield put(updateSpot({ id: response.spot_id, authorizedDomains: response.domains }));
  } catch (reason) {
    ErrorNotifier.notifyError(`Failed to fetch authorized domains`);
  }
}

function* addAuthorizedDomainSaga(action: PayloadAction<string>) {
  const domain = action.payload;

  try {
    const response: FetchAuthorizedDomainsResponse = yield call(addAuthorizedDomainRequest, domain);

    const { spot_id, domains } = response;
    const index = domains.indexOf(domain);
    domains.splice(index, 1);
    domains.unshift(domain);

    yield put(updateSpot({ id: spot_id, authorizedDomains: domains }));
  } catch (reason) {
    let toast: ShowToastPayload;
    if (reason.status === 400) {
      toast = { type: 'error', message: 'Wildcards are allowed only as subdomains' };
    } else {
      toast = { type: 'error', message: 'Failed to add authorized domain' };
    }
    yield put(showToast(toast));
  }
}

function* removeAuthorizedDomainSaga(action: PayloadAction<string>) {
  const domain = action.payload;

  try {
    const response: FetchAuthorizedDomainsResponse = yield call(removeAuthorizedDomainRequest, domain);

    const { spot_id, domains } = response;

    yield put(updateSpot({ id: spot_id, authorizedDomains: domains }));
  } catch (reason) {
    ErrorNotifier.notifyError(`Failed to remove authorized domain`);
  }
}

function* fetchSpotModerationDataSaga(action: PayloadAction<string>) {
  const spotId = action.payload;
  const spot = yield select((state: State) => state.spots.map[spotId]);

  if (spot && spot.isFetched) {
    return;
  }

  try {
    const response: FetchModerationPreferenceResponse = yield call(getModerationPreferences, spotId);

    yield put(updateSpot(mapToSpotProps(response)));

    yield call(fetchHighlightTags, spotId, spot.mainLanguage);
  } catch (reason) {
    ErrorNotifier.notifyError('Failed fetch spot moderation preferences');
  }
}

function* fetchSpotPolicyDataSaga(action: PayloadAction<string>) {
  const spotId = action.payload;
  try {
    const v1Config = yield call(getV1Config, spotId, MODERATION_SETTINGS_CONFIG_FIELDS);

    const { tenant } = yield call(getSpotConfig, spotId, [
      'tenant.moderation.spot_policies',
      'tenant.moderation.block_report_by_registered',
      'tenant.moderation.strict_external_urls_enabled'
    ]);

    const tenantSpotPolicies = tenant?.moderation?.spot_policies?.value;
    const blockedByRegistered = tenant?.moderation?.block_report_by_registered?.value;
    const strictExternalUrlsEnabled = tenant?.moderation?.strict_external_urls_enabled?.value;

    const allConfigs = {
      ...v1Config,
      default: tenantSpotPolicies?.default,
      image: tenantSpotPolicies?.image,
      external_link: tenantSpotPolicies?.external_link,
      animation: tenantSpotPolicies?.animation || tenantSpotPolicies?.default,
      block_report_by_registered: blockedByRegistered,
      strict_external_urls_enabled: strictExternalUrlsEnabled
    };

    const parsed = mapToSpotProps(allConfigs);

    yield put(updateSpot(parsed));
  } catch (reason) {
    ErrorNotifier.notifyError(`Failed to fetch spot settings`);
  }
}

function* setStrictExternalUrlsFlagSaga(action: PayloadAction<SetStrictExternalUrlsFlagPayload>) {
  const { spotId, value } = action.payload;

  try {
    yield call(setSpotConfig, spotId, [{ key: 'tenant.moderation.strict_external_urls_enabled', value }]);
    yield put(updateSpot({ id: spotId, strictExternalUrlsEnabled: value }));
  } catch (reason) {
    ErrorNotifier.notifyError(`Failed to set strict external urls flag`);
  }
}

export function* spotsSagas() {
  yield all([
    takeLatest(fetchInitSpotData, fetchInitSpotDataSaga),
    takeLatest(editSpot, editSpotSaga),
    takeLatest(fetchAuthorizedDomains, fetchAuthorizedDomainsSaga),
    takeLatest(addAuthorizedDomain, addAuthorizedDomainSaga),
    takeLatest(removeAuthorizedDomain, removeAuthorizedDomainSaga),
    takeLatest(fetchSpotModerationData, fetchSpotModerationDataSaga),
    takeLatest(fetchSpotPolicyData, fetchSpotPolicyDataSaga),
    takeLatest(setStrictExternalUrlsFlag, setStrictExternalUrlsFlagSaga)
  ]);
}
